import {
    areSearchParamsEqual,
    SKILL_GROUP_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type SkillGroupFilterItem = { key: string; value: string };

export type SkillGroupUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterStatusesId: SkillGroupFilterItem | null;
    filterStatusName: SkillGroupFilterItem | null;
    filterTypeId: SkillGroupFilterItem | null;
    uiFilters: Record<string, any>;
};

export type SkillGroupUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterStatusesId: SkillGroupFilterItem | null;
    filterStatusName: SkillGroupFilterItem | null;
    filterTypeId: SkillGroupFilterItem | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialSkillGroupUrlState(): SkillGroupUrlViewState {
    return parseSkillGroupUrlState(getHashSearchParams());
}

export function parseSkillGroupUrlState(params: URLSearchParams): SkillGroupUrlViewState {
    params = unwrapEncodedSearchParams(params, SKILL_GROUP_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const name = params.get('name');
    const isActiveRaw = params.get('isActive');
    const isActive = isActiveRaw == null ? null : toTrueFalseString(isActiveRaw);

    if (id) uiFilters.id = id;
    if (name) uiFilters.name = name;
    if (isActive) uiFilters.isActive = isActive;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterStatusesId: toFilterItem('Id', id),
        filterStatusName: toFilterItem('Name', name),
        filterTypeId: toFilterItem('IsActive', isActive),
        uiFilters,
    };
}

export function buildSkillGroupSearchParams(state: SkillGroupUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainSkillGroupSearchParams(state), SKILL_GROUP_ENCODED_QUERY_KEY);
}

function buildPlainSkillGroupSearchParams(state: SkillGroupUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterStatusesId?.value);
    setScalar(params, 'name', state.filterStatusName?.value);
    if (state.filterTypeId?.value !== undefined && state.filterTypeId?.value !== null && state.filterTypeId.value !== '') {
        params.set('isActive', normalizeBooleanParam(state.filterTypeId.value));
    }

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function toFilterItem(key: string, value?: string | null): SkillGroupFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toTrueFalseString(value: string): string | null {
    if (value === '1' || value === 'true') return 'true';
    if (value === '0' || value === 'false') return 'false';
    return null;
}

function normalizeBooleanParam(value: string): string {
    if (value === 'true') return '1';
    if (value === 'false') return '0';
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
